import {
  breakpoint,
  TextArea,
  PrimaryButton,
  FormDropDown,
  Field,
  BodyText,
  SpinnerIcon,
  UIText,
} from '@grays-npm/component-library';
import styled from 'styled-components';
import { useAuthData } from '../Auth';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { StyledHeading as Heading } from '../../pages/styles';
import { useMutation } from 'react-query';
import { ApiError } from '../../services/apiError';
import {
  submitGetInTouch,
  GetInTouchReturn,
  GetInTouchRequest,
  issueTypes,
} from '../../services/getInTouch';
import { useModal } from '../../state/modal';
import { useCMSData } from '../../state/content-manager';

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: ${({ theme }) => theme.spacings[4].rem};
  margin-top: ${({ theme }) => theme.spacings[4].rem};

  ${breakpoint('sm')`
      grid-template-columns: 1fr 1fr;
  `};
`;

const StyledBody = styled(BodyText)`
  margin-bottom: ${({ theme }) => theme.spacings[5].rem};
`;

const StyledHeading = styled(Heading)`
  margin-top: ${({ theme }) => theme.spacings[5].rem};
  margin-bottom: ${({ theme }) => theme.spacings[5].rem};
  color: ${({ theme }) => theme.colors.primary.graysBlue};

  ${breakpoint('md')`
      margin-top: 0;
  `};
`;

const Spinner = styled(SpinnerIcon)`
  fill: ${({ theme }) => theme.colors.primary.white};
`;

type GetInTouchProps = {
  lotSku: string;
};

export const GetInTouch: React.FC<GetInTouchProps> = ({ lotSku }) => {
  const cmsData = useCMSData();
  console.log(cmsData);
  const { token, firstName, lastName, email } = useAuthData();
  const { setModalProps } = useModal();

  const getInTouchMutation = useMutation<
    GetInTouchReturn,
    ApiError,
    GetInTouchRequest
  >((GetInTouchRequest) => submitGetInTouch(GetInTouchRequest, token), {
    onSuccess: (data) => {
      setModalProps({
        onClose: () => {},
        isOpen: true,
        title: data.message,
        children: <UIText size="md">{data.message}</UIText>,
      });
    },
    onError: (data) => {
      setModalProps({
        onClose: () => {},
        isOpen: true,
        title: '',
        children: <UIText size="md">{data.message}</UIText>,
      });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<GetInTouchRequest>({
    mode: 'all',
    defaultValues: {
      firstName,
      lastName,
      email,
    },
    reValidateMode: 'onChange',
  });

  const onSubmit: SubmitHandler<GetInTouchRequest> = (data) => {
    getInTouchMutation.mutate({ ...data, lotSku });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StyledHeading>{cmsData.modals.contact.title}</StyledHeading>
      <StyledBody>{cmsData.modals.contact.body}</StyledBody>
      <FieldGrid>
        <Field
          isCompact={false}
          label="First Name"
          {...register('firstName', {
            required: {
              value: true,
              message: 'This is required',
            },
          })}
          errorMessage={errors.firstName?.message}
          type="string"
          size="md"
          placeholder="First Name"
          controlled={false}
          value=""
          required
        />
        <Field
          {...register('lastName', {
            required: {
              value: true,
              message: 'This is required',
            },
          })}
          isCompact={false}
          label="Last Name"
          errorMessage={errors.lastName?.message}
          type="string"
          size="md"
          placeholder="Last Name"
          controlled={false}
          required
        />
        <Field
          {...register('email', {
            required: {
              value: true,
              message: 'This is required',
            },
            pattern: {
              value: /[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$/,
              message: 'Invalid email address',
            },
          })}
          isCompact={false}
          label="Email"
          errorMessage={errors.email?.message}
          type="string"
          size="md"
          placeholder="Email"
          controlled={false}
          required
        />
        <Field
          {...register('mobile', {
            required: {
              value: true,
              message: 'This is required',
            },
          })}
          isCompact={false}
          label="Mobile"
          errorMessage={errors.mobile?.message}
          type="string"
          size="md"
          placeholder="Mobile"
          controlled={false}
          required
        />
        <Controller
          control={control}
          name="issueType"
          rules={{ required: 'This is required' }}
          render={({ field: { onChange, value } }) => (
            <FormDropDown
              isCompact={false}
              label="Type"
              required
              options={issueTypes}
              onSelect={(e) => onChange(e)}
              selectedOption={value}
              errorMessage={errors.issueType?.message}
            />
          )}
        />
      </FieldGrid>
      <br />
      <Controller
        control={control}
        name="message"
        rules={{
          required: {
            value: true,
            message: 'This is required',
          },
          minLength: {
            value: 30,
            message: 'Minimum 30 characters',
          },
        }}
        render={({ field: { onChange, value } }) => (
          <TextArea
            value={value}
            isCompact
            required
            label="Tell us about what you have to sell"
            onChange={(e) => onChange(e)}
            errorMessage={errors.message?.message}
          />
        )}
      />
      <PrimaryButton
        onClick={handleSubmit(onSubmit)}
        disabled={getInTouchMutation.isLoading && true}
        size="large"
      >
        {getInTouchMutation.isLoading ? <Spinner width={16} /> : 'Submit'}
      </PrimaryButton>
    </form>
  );
};
